import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { API_ENDPOINTS, API_BASE_URL } from '../api/config';

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (ctx) return ctx;
  // If hook is used outside of provider (tests or isolated components),
  // return a safe default shape to avoid runtime errors.
  return {
    user: null,
    isAuthenticated: false,
    loading: false,
    twoFactorPending: null,
    networkStatus: 'online',
    login: async () => ({ success: false }),
    logout: () => {},
    register: async () => ({ success: false }),
    verify2FA: async () => ({ success: false }),
    cancel2FA: () => {},
    refreshUser: async () => {},
    ensureFreshTokens: async () => false,
    toggleSubscription: () => {},
    updateSubscription: () => {},
    checkSupportTickets: () => {},
    updateUserSettings: async () => ({ success: false })
  };
};

export const AuthProvider = ({ children }) => {
  // Initialize twoFactorPending from sessionStorage if available
  const getInitialTwoFactorPending = () => {
    try {
      const stored = sessionStorage.getItem('twoFactorPending');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error parsing stored 2FA data:', error);
      sessionStorage.removeItem('twoFactorPending');
      return null;
    }
  };

  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [twoFactorPending, setTwoFactorPending] = useState(getInitialTwoFactorPending);
  const [pendingLoginCredentials, setPendingLoginCredentials] = useState(null);
  const [networkStatus, setNetworkStatus] = useState('online'); // 'online', 'unstable', 'offline'
  const [lastPageLeaveTime, setLastPageLeaveTime] = useState(null); // Track when user left the page
  const [lastTokenRefresh, setLastTokenRefresh] = useState(Date.now()); // Track last token refresh time

// In AuthContext.js - on app initialization
useEffect(() => {
  const checkExistingAuth = async () => {
    // Skip auto-login if user explicitly wants to bypass dashboard redirect (e.g., clicked "back to home")
    if (sessionStorage.getItem('bypassDashboardRedirect')) {
      console.log('🔄 Skipping dev auto-login due to bypassDashboardRedirect flag');
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    // In development, automatically bypass authentication for easier testing
    if (process.env.NODE_ENV === 'development') {
      // Check for saved dev subscription preference
      const savedDevSubscription = localStorage.getItem('dev-subscription');
      // Default dev AI tier to 'free' so local admin starts without elevated AI by default
      const savedDevAiTier = localStorage.getItem('dev-ai-tier') || 'free';
      
      // Load persisted user settings
      const persistedSettings = JSON.parse(localStorage.getItem('dev-user-settings') || '{}');
      
      const mockUser = {
        id: 'dev-admin-user-123',
        name: 'Dev Admin User',
        email: 'admin@planmysemester.com',
        role: 'admin', // Admin role for testing
        subscription: savedDevSubscription || 'free', // Use saved subscription or default to 'free' for dev
        aiTier: savedDevAiTier, // Use saved AI tier or default to 'free'
        aiTokensLimit: 100000, // 100K tokens for free tier
        aiTokensUsed: 0, // Fresh tokens
        aiTokensResetDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1), // Next month
        stripeSubscriptionId: 'mock-stripe-subscription-123', // Mock Stripe subscription ID for testing toggle
        stripeCustomerId: 'mock-stripe-customer-123', // Mock Stripe customer ID
        subscriptionStatus: 'active', // Active subscription status
        cancelAtPeriodEnd: false, // Auto-renewal enabled
        twoFactorEnabled: false, // Set to false to test 2FA warning
        settings: {
          // Merge default settings with persisted settings
          notifications: {
            email: true,
            push: true,
            dueDateReminders: true,
            reminderTime: 24,
            ...persistedSettings.notifications
          },
          preferences: {
            defaultView: 'dashboard',
            timezone: 'UTC',
            dateFormat: 'MM/DD/YYYY',
            timeFormat: '12h',
            weekStart: 'sunday',
            semesterMiddleLabel: 'spring',
            ...persistedSettings.preferences
          },
          display: {
            compactMode: false,
            showCompletedAssignments: false,
            ...persistedSettings.display
          },
          gpa: {
            A: { min: 93, max: 100, value: 4.0 },
            'A-': { min: 90, max: 92, value: 3.67 },
            'B+': { min: 87, max: 89, value: 3.33 },
            B: { min: 83, max: 86, value: 3.0 },
            'B-': { min: 80, max: 82, value: 2.67 },
            'C+': { min: 77, max: 79, value: 2.33 },
            C: { min: 73, max: 76, value: 2.0 },
            'C-': { min: 70, max: 72, value: 1.67 },
            'D+': { min: 67, max: 69, value: 1.33 },
            D: { min: 60, max: 66, value: 1.0 },
            F: { min: 0, max: 59, value: 0.0 },
            ...persistedSettings.gpa
          },
          // Include any other persisted settings
          ...persistedSettings
        }
      };
      setUser(mockUser);
      setIsAuthenticated(true);
      setLoading(false);
      return;
    }
    
    // Check if user recently logged out - don't attempt re-authentication
    const logoutTimestamp = sessionStorage.getItem('logoutTimestamp');
    const now = Date.now();
    const recentlyLoggedOut = logoutTimestamp && (now - parseInt(logoutTimestamp)) < 60000; // 60 seconds (increased from 30)
    
    // Check multiple logout indicators for stronger protection
    const fromLogout = sessionStorage.getItem('fromLogout');
    const logoutProtectionActive = fromLogout || recentlyLoggedOut;
    
    if (logoutProtectionActive) {
      console.log('🔒 STRONG LOGOUT PROTECTION ACTIVE - Skipping authentication check', {
        hasFromLogoutFlag: !!fromLogout,
        logoutTimestamp,
        timeSinceLogout: logoutTimestamp ? now - parseInt(logoutTimestamp) : 'N/A',
        recentlyLoggedOut,
        protectionActive: logoutProtectionActive
      });
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }
    
    // Check authentication server-side using cookies only
    // Verify authentication server-side to ensure cookies are still valid
    try {
      // Add timeout to prevent indefinite loading
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout for slower connections
      
      const response = await fetch(API_ENDPOINTS.VERIFY, {
        credentials: 'include', // Send cookies for server-side validation
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        // Update user data with fresh data from server
        setUser(data.user);
        // Clear bypass flag on successful authentication
        sessionStorage.removeItem('bypassDashboardRedirect');
        setIsAuthenticated(true);
        // Clear any stale 2FA pending state on successful authentication
        if (twoFactorPending) {
          setTwoFactorPending(null);
          sessionStorage.removeItem('twoFactorPending');
        }
        // Clear logout protection flags on successful authentication
        sessionStorage.removeItem('fromLogout');
        sessionStorage.removeItem('logoutTimestamp');
        // Check for unhandled support tickets for support/admin users
        setTimeout(() => checkSupportTickets(), 1000); // Small delay to ensure state is updated
      } else {
        // Authentication failed, clear user data
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Authentication verification failed:', error);
      // Clear authentication on error
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  checkExistingAuth();
}, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (email, password, totpToken = null) => {
    try {
      const body = { email, password };
      if (totpToken) {
        body.totpToken = totpToken;
      }

      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Include cookies
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (data.requires2FA) {
          // Store pending 2FA data (only serializable fields)
          const twoFactorData = {
            email: data.user.email,
            userId: data.user.id,
            userName: data.user.name
          };
          setTwoFactorPending(twoFactorData);
          setPendingLoginCredentials({ email, password });
          sessionStorage.setItem('twoFactorPending', JSON.stringify(twoFactorData));
          return { success: true, requires2FA: true, user: data.user };
        } else {
          // Tokens are now handled server-side via HTTP-only cookies
          // No localStorage storage needed - eliminates XSS vulnerability
          
          setUser(data.user);
          // Clear bypass flag on successful login
          sessionStorage.removeItem('bypassDashboardRedirect');
          setIsAuthenticated(true);
          // Clear the logout flag since user has successfully logged in
          sessionStorage.removeItem('fromLogout');
          // Sync with latest Stripe data immediately after login
          await refreshUser();
          // Check for unhandled support tickets for support/admin users
          setTimeout(() => checkSupportTickets(), 1000);
          return { success: true, user: data.user };
        }
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const cancel2FA = () => {
    setTwoFactorPending(null);
    setPendingLoginCredentials(null);
    sessionStorage.removeItem('twoFactorPending');
  };

  const verify2FA = async (token) => {
    try {
      if (!twoFactorPending) {
        return { success: false, error: 'No 2FA verification pending' };
      }

      if (!pendingLoginCredentials) {
        return { success: false, error: 'Session expired. Please login again.' };
      }

      // Use the stored credentials + token to login via the main login endpoint
      // This ensures password is verified along with the TOTP token
      const result = await login(pendingLoginCredentials.email, pendingLoginCredentials.password, token);

      if (result.success) {
        setTwoFactorPending(null);
        setPendingLoginCredentials(null);
        sessionStorage.removeItem('twoFactorPending');
        return result;
      } else {
        return result;
      }
    } catch (error) {
      console.error('2FA verification error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const register = async (name, email, password, enable2FA = false) => {
    try {
      const response = await fetch(API_ENDPOINTS.REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Include cookies
        body: JSON.stringify({ name, email, password, enable2FA })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Tokens are now handled server-side via HTTP-only cookies
        // No localStorage storage needed - eliminates XSS vulnerability
        
        setUser(data.user);
        setIsAuthenticated(true);
        // Clear the logout flag since user has successfully registered
        sessionStorage.removeItem('fromLogout');
        return { success: true, user: data.user, twoFactor: data.twoFactor };
      } else {
        return { success: false, error: data.error || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const logout = useCallback(async () => {
    try {
      // Set sessionStorage flag FIRST to prevent any re-authentication
      sessionStorage.setItem('fromLogout', 'true');
      sessionStorage.setItem('logoutTimestamp', Date.now().toString());
      sessionStorage.setItem('bypassDashboardRedirect', 'true');

      // Clear authentication state immediately
      setUser(null);
      setIsAuthenticated(false);

      console.log('🚪 FRONTEND LOGOUT: Starting logout process');

      // Call logout endpoint to clear server-side cookies - with timeout
      const logoutPromise = fetch(API_ENDPOINTS.LOGOUT, {
        method: 'POST',
        credentials: 'include'
      });

      // Set a timeout for the logout request (5 seconds)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Logout request timeout')), 5000);
      });

      try {
        const response = await Promise.race([logoutPromise, timeoutPromise]);
        console.log('🚪 FRONTEND LOGOUT: Backend response:', {
          status: response.status,
          ok: response.ok,
          statusText: response.statusText
        });

        if (response.ok) {
          const data = await response.json();
          console.log('🚪 FRONTEND LOGOUT: Backend logout successful:', data);
        } else {
          console.warn('Backend logout failed, but proceeding with frontend logout');
        }
      } catch (networkError) {
        console.warn('🚪 FRONTEND LOGOUT: Network error during logout, proceeding with frontend cleanup:', networkError.message);
      }

      // AGGRESSIVE frontend cookie clearing - always execute this
      const cookieNames = ['token', 'refreshToken', 'dev_token', 'dev_refreshToken'];
      const domains = ['', '.planmysemester.xyz', 'planmysemester.xyz'];
      const paths = ['/', '/api'];

      try {
        // Clear all combinations of cookies
        cookieNames.forEach(name => {
          domains.forEach(domain => {
            paths.forEach(path => {
              // Multiple clearing methods
              document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; ${domain ? `domain=${domain};` : ''}`;
              document.cookie = `${name}=; max-age=0; path=${path}; ${domain ? `domain=${domain};` : ''}`;
              document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; samesite=lax; ${domain ? `domain=${domain};` : ''}`;
              document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; samesite=strict; ${domain ? `domain=${domain};` : ''}`;
            });
          });
        });

        // Clear any potential auth-related localStorage items
        localStorage.removeItem('dev-subscription');
        localStorage.removeItem('dev-ai-tier');

      } catch (cookieError) {
        console.warn('Frontend cookie clearing failed:', cookieError);
      }

      console.log('✅ Logout completed successfully - all tokens and cookies cleared');

    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, we've already cleared local state and set the logout flag
      // This ensures the user appears logged out on the frontend
    }
  }, []);

  const refreshUser = useCallback(async (retryCount = 0) => {
    const maxRetries = 3;
    const baseDelay = 500; // 500ms

    try {
      console.log(`🔄 Refreshing user data... (attempt ${retryCount + 1})`);
      const response = await fetch(API_ENDPOINTS.VERIFY, {
        credentials: 'include',
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        console.log('✅ User data refreshed successfully');
      } else if (response.status === 401) {
        // Token is invalid, logout immediately
        console.log('🚫 Token invalid during user refresh, logging out');
        logout();
      } else {
        console.error(`❌ User refresh failed with status: ${response.status}`);

        // Retry on server errors
        if (response.status >= 500 && retryCount < maxRetries) {
          const delay = baseDelay * Math.pow(2, retryCount);
          console.log(`🔄 Retrying user refresh in ${delay}ms due to server error...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return refreshUser(retryCount + 1);
        }
      }
    } catch (error) {
      console.warn(`User refresh attempt ${retryCount + 1} failed:`, error.message);

      // Retry on network errors
      if (retryCount < maxRetries && (
        error.name === 'TypeError' || // Network errors
        error.name === 'AbortError' || // Timeout errors
        error.message.includes('fetch') // Fetch-related errors
      )) {
        const delay = baseDelay * Math.pow(2, retryCount);
        console.log(`🔄 Retrying user refresh in ${delay}ms due to network error...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return refreshUser(retryCount + 1);
      }

      console.error('❌ User refresh failed after retries - keeping current user data');
      // Don't logout on network errors for user refresh - just keep current data
    }
  }, [logout]);

  const updateUserSettings = useCallback(async (settingsUpdate) => {
    try {
      // Handle mock user in development mode
      if (process.env.NODE_ENV === 'development' && user?.id === 'dev-admin-user-123') {
        console.log('🔧 Development mode: Updating mock user settings locally');

        // Update local user state
        setUser(prevUser => {
          const updatedUser = {
            ...prevUser,
            settings: {
              ...prevUser.settings,
              ...settingsUpdate
            }
          };

          // Persist settings to localStorage for mock user
          const existingSettings = JSON.parse(localStorage.getItem('dev-user-settings') || '{}');
          const newSettings = { ...existingSettings, ...settingsUpdate };
          localStorage.setItem('dev-user-settings', JSON.stringify(newSettings));

          return updatedUser;
        });

        console.log('✅ Mock user settings updated successfully');
        return { success: true };
      }

      console.log('🔄 Updating user settings via API...');
      const response = await fetch(API_ENDPOINTS.SETTINGS, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ settings: settingsUpdate })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Update local user state
        setUser(prevUser => ({
          ...prevUser,
          settings: {
            ...prevUser.settings,
            ...settingsUpdate
          }
        }));
        console.log('✅ User settings updated successfully');
        return { success: true };
      } else {
        console.error('❌ Settings update failed:', data.message);
        return { success: false, error: data.message || 'Failed to update settings' };
      }
    } catch (error) {
      console.error('❌ Settings update error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }, [user?.id]);

  // Utility function to proactively refresh tokens if needed
  const ensureFreshTokens = useCallback(async () => {
    // In development mode with mock user, skip token refresh
    if (process.env.NODE_ENV === 'development' && user?.id === 'dev-admin-user-123') {
      console.log('🔧 Development mode: Skipping token refresh for mock user');
      return true; // Mock user tokens are always "fresh"
    }

    try {
      console.log('🔍 Checking token freshness...');
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('✅ Tokens refreshed proactively');
          setLastTokenRefresh(Date.now());
          await refreshUser();
          return true;
        }
      }
      console.log('❌ Token refresh failed');
      return false;
    } catch (error) {
      console.error('❌ Token refresh error:', error);
      return false;
    }
  }, [refreshUser, user?.id]);

  // Utility function to check if current token is expired with retry logic
  const isTokenExpired = useCallback(async (retryCount = 0) => {
    // In development mode with mock user, skip token verification
    if (process.env.NODE_ENV === 'development' && user?.id === 'dev-admin-user-123') {
      console.log('🔧 Development mode: Skipping token verification for mock user');
      return false; // Mock user is never "expired"
    }

    const maxRetries = 5;
    const baseDelay = 1000; // 1 second

    try {
      // Make a lightweight API call to test token validity
      const response = await fetch(API_ENDPOINTS.VERIFY_TOKEN, {
        method: 'GET',
        credentials: 'include',
        // Add timeout to prevent hanging on slow/unstable connections
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });

      // Record success
      if (window._authNetworkMonitor) {
        window._authNetworkMonitor.recordSuccess();
      }

      return !response.ok; // If response is not ok, token is expired/invalid
    } catch (error) {
      console.warn(`Token verification attempt ${retryCount + 1} failed:`, error.message);

      // Record network failure
      if (window._authNetworkMonitor) {
        window._authNetworkMonitor.recordFailure();
      }

      // If this is a network error and we haven't exceeded max retries, try again
      if (retryCount < maxRetries && (
        error.name === 'TypeError' || // Network errors
        error.name === 'AbortError' || // Timeout errors
        error.message.includes('fetch') // Fetch-related errors
      )) {
        const delay = baseDelay * Math.pow(2, retryCount); // Exponential backoff
        console.log(`🔄 Retrying token verification in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return isTokenExpired(retryCount + 1);
      }

      // If we've exhausted retries or it's not a network error, assume token is expired
      console.error('❌ Token verification failed after retries, assuming expired');
      return true;
    }
  }, [user?.id]);

  // Enhanced token refresh with retry logic and network resilience
  const refreshTokens = useCallback(async (retryCount = 0) => {
    const maxRetries = 5;
    const baseDelay = 1000; // 1 second

    try {
      console.log(`🔄 Auto-refreshing tokens... (attempt ${retryCount + 1})`);
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        // Add timeout for unstable connections
        signal: AbortSignal.timeout(15000) // 15 second timeout for refresh
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('✅ Tokens auto-refreshed successfully');

          // Record success
          if (window._authNetworkMonitor) {
            window._authNetworkMonitor.recordSuccess();
          }

          setLastTokenRefresh(Date.now());

          // Small delay to ensure cookies are set before making next API call
          await new Promise(resolve => setTimeout(resolve, 100));

          // Optionally refresh user data after token refresh
          await refreshUser();
        } else {
          console.error('❌ Token refresh failed:', data.error);
          // If refresh fails, logout the user
          logout();
        }
      } else {
        console.error(`❌ Token refresh HTTP error: ${response.status}`);

        // Record failure for server errors
        if (window._authNetworkMonitor && response.status >= 500) {
          window._authNetworkMonitor.recordFailure();
        }

        // For network/server errors, retry before giving up
        if (response.status >= 500 && retryCount < maxRetries) {
          const delay = baseDelay * Math.pow(2, retryCount);
          console.log(`🔄 Retrying token refresh in ${delay}ms due to server error...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return refreshTokens(retryCount + 1);
        }

        // If refresh fails due to network or auth issues, logout
        logout();
      }
    } catch (error) {
      console.warn(`Token refresh attempt ${retryCount + 1} failed:`, error.message);

      // Record network failure
      if (window._authNetworkMonitor) {
        window._authNetworkMonitor.recordFailure();
      }

      // Retry on network errors
      if (retryCount < maxRetries && (
        error.name === 'TypeError' || // Network errors
        error.name === 'AbortError' || // Timeout errors
        error.message.includes('fetch') // Fetch-related errors
      )) {
        const delay = baseDelay * Math.pow(2, retryCount);
        console.log(`🔄 Retrying token refresh in ${delay}ms due to network error...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return refreshTokens(retryCount + 1);
      }

      console.error('❌ Token refresh failed after retries');
      // On network errors, logout to be safe (but only after retries)
      logout();
    }
  }, [refreshUser, logout]);

  // Automatic token refresh every 45 minutes when authenticated
  useEffect(() => {
    // Skip token refresh setup for development mock user
    if (process.env.NODE_ENV === 'development' && user?.id === 'dev-admin-user-123') {
      console.log('🔧 Development mode: Skipping token refresh setup for mock user');
      return;
    }

    if (!isAuthenticated || !user) {
      return; // Don't set up refresh if not authenticated
    }

    console.log('🔄 Setting up automatic token refresh (every 45 minutes)');

    // Set up interval for regular refresh
    const refreshInterval = setInterval(refreshTokens, 2700000); // 45 minutes

    // Also refresh when user returns to the tab (handles long idle periods)
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const timeAway = lastPageLeaveTime ? (now - lastPageLeaveTime) / 1000 / 60 : 0; // minutes away
        const timeSinceLastRefresh = (now - lastTokenRefresh) / 1000 / 60; // minutes since last refresh

        console.log(`👀 User returned to tab after ${timeAway.toFixed(1)} minutes away (${timeSinceLastRefresh.toFixed(1)} minutes since last refresh)`);

        // Check if current token is still valid
        const tokenExpired = await isTokenExpired();

        if (tokenExpired) {
          console.log('⏰ Token expired while away, logging out');
          logout();
        } else {
          // Refresh if user was away for 5+ minutes OR token expires within 10 minutes (50+ minutes since refresh)
          const shouldRefresh = timeAway >= 5 || timeSinceLastRefresh >= 50;
          if (shouldRefresh) {
            console.log(`✅ ${timeAway >= 5 ? 'User was away for 5+ minutes' : 'Token expires within 10 minutes'}, refreshing tokens...`);
            refreshTokens();
          } else {
            console.log('✅ User was away briefly and token is fresh, skipping immediate refresh');
          }
        }
      } else {
        // User is leaving the page
        setLastPageLeaveTime(Date.now());
      }
    };

    // Also refresh on window focus for additional reliability
    const handleWindowFocus = async () => {
      const now = Date.now();
      const timeAway = lastPageLeaveTime ? (now - lastPageLeaveTime) / 1000 / 60 : 0; // minutes away
      const timeSinceLastRefresh = (now - lastTokenRefresh) / 1000 / 60; // minutes since last refresh

      console.log(`🎯 Window focused after ${timeAway.toFixed(1)} minutes away (${timeSinceLastRefresh.toFixed(1)} minutes since last refresh)`);

      // Check if current token is still valid
      const tokenExpired = await isTokenExpired();

      if (tokenExpired) {
        console.log('⏰ Token expired while away, logging out');
        logout();
      } else {
        // Refresh if user was away for 5+ minutes OR token expires within 10 minutes (50+ minutes since refresh)
        const shouldRefresh = timeAway >= 5 || timeSinceLastRefresh >= 50;
        if (shouldRefresh) {
          console.log(`✅ ${timeAway >= 5 ? 'User was away for 5+ minutes' : 'Token expires within 10 minutes'}, refreshing tokens...`);
          refreshTokens();
        } else {
          console.log('✅ User was away briefly and token is fresh, skipping immediate refresh');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    // Network status monitoring for unstable connections
    const handleOnline = () => {
      console.log('🌐 Network connection restored');
      setNetworkStatus('online');
      // When connection is restored, check if we need to refresh tokens
      if (isAuthenticated && user) {
        setTimeout(() => refreshTokens(), 1000); // Delay to ensure connection is stable
      }
    };

    const handleOffline = () => {
      console.log('📶 Network connection lost');
      setNetworkStatus('offline');
    };

    // Monitor for unstable connections (repeated failures)
    let consecutiveFailures = 0;
    const monitorNetworkStability = () => {
      if (consecutiveFailures >= 2) {
        setNetworkStatus('unstable');
        console.log('⚠️ Network detected as unstable - enabling retry logic');
      } else if (consecutiveFailures === 0) {
        setNetworkStatus('online');
      }
    };

    // Expose network monitoring to token functions
    window._authNetworkMonitor = {
      recordFailure: () => {
        consecutiveFailures++;
        monitorNetworkStability();
      },
      recordSuccess: () => {
        consecutiveFailures = Math.max(0, consecutiveFailures - 1);
        monitorNetworkStability();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Cleanup function to clear interval and remove listeners
    return () => {
      console.log('🧹 Clearing automatic token refresh interval');
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      delete window._authNetworkMonitor;
    };
  }, [isAuthenticated, user, logout, refreshUser, isTokenExpired, refreshTokens, lastPageLeaveTime, lastTokenRefresh]); // Dependencies: restart interval if auth state changes

  const toggleSubscription = () => {
    if (user && user.id === 'dev-admin-user-123') {
      const subscriptions = ['free', 'monthly', 'quarterly', 'yearly'];
      const currentIndex = subscriptions.indexOf(user.subscription);
      const nextIndex = (currentIndex + 1) % subscriptions.length;
      const newSubscription = subscriptions[nextIndex];
      const updatedUser = { ...user, subscription: newSubscription };
      setUser(updatedUser);
    }
  };

  const checkSupportTickets = useCallback(async () => {
    // Only check for support/admin users
    if (!user || (user.role !== 'support' && user.role !== 'admin')) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE}/support/admin/tickets?status=open&unresponded=true`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.tickets && data.tickets.length > 0) {
          const ticketCount = data.tickets.length;
          toast.info(
            `You have ${ticketCount} unhandled support ticket${ticketCount > 1 ? 's' : ''} waiting for response.`,
            {
              position: "top-right",
              autoClose: 8000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              theme: "light",
              onClick: () => {
                // Navigate to support page when clicked
                window.location.href = '/support';
              }
            }
          );
        }
      }
    } catch (error) {
      console.error('Failed to check support tickets:', error);
    }
  }, [user]);

  const updateSubscription = (newSubscription, newAiTier = null) => {
    if (user) {
      const updatedUser = { 
        ...user, 
        subscription: newSubscription,
        aiTier: newAiTier !== null ? newAiTier : user.aiTier
      };
      setUser(updatedUser);
      
      // For dev accounts, persist the subscription choice to localStorage
      if (process.env.NODE_ENV === 'development' && user.id === 'dev-admin-user-123') {
        if (newSubscription !== null) {
          localStorage.setItem('dev-subscription', newSubscription);
        } else {
          localStorage.removeItem('dev-subscription');
        }
        
        if (newAiTier !== null) {
          localStorage.setItem('dev-ai-tier', newAiTier);
        } else if (newAiTier === 'free') {
          localStorage.setItem('dev-ai-tier', 'free');
        }
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, twoFactorPending, networkStatus, login, logout, register, verify2FA, cancel2FA, refreshUser, ensureFreshTokens, toggleSubscription, updateSubscription, checkSupportTickets, updateUserSettings }}>
      {children}
    </AuthContext.Provider>
  );
};